'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiGoalType, FactiiiRequirement, type PostFactiii } from '@shared/all/domains/factiiis';
import { postDataSort } from '@shared/all/domains/posts';
import { type PostFeedInput } from '@shared/all/validators';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import getTimeBySort from '@shared/utils/helpers/getTimeBySort';
import groupByFactiiiId from '@shared/utils/helpers/groupByFactiiiId';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import { NewPostModal } from './NewPost/NewPostModal';
type Props = {
  initialFactiiiData: PostFactiii[][];
  params: PostFeedInput;
};
const DataItem = props => {
  const $ = _c(85);
  const [expanded, setExpanded] = useState(false);
  const factiii = props.initialFactiiiData[0][0];
  const {
    queryKey
  } = usePostsShared(client);
  const currentUser = useUserIdentity();
  const {
    getFactiiiId
  } = useFactiiis(client);
  const {
    localPreferenceState,
    getPostParams
  } = useLocalPreference();
  const [newPostModalOpen, setNewPostModalOpen] = useState(false);
  const t0 = factiii.factiiiId;
  let t1;
  if ($[0] !== factiii.factiiiId || $[1] !== getPostParams || $[2] !== props.params) {
    t1 = getPostParams(props.params, undefined, [factiii.factiiiId]);
    $[0] = factiii.factiiiId;
    $[1] = getPostParams;
    $[2] = props.params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== factiii.factiiiId || $[5] !== queryKey || $[6] !== t1) {
    t2 = [queryKey, t0, t1];
    $[4] = factiii.factiiiId;
    $[5] = queryKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== factiii.factiiiId || $[9] !== getPostParams || $[10] !== props.params) {
    t3 = async t4 => {
      const {
        pageParam
      } = t4;
      const {
        posts
      } = await unwrapFromResult(client.posts.feed.query(getPostParams(props.params, pageParam, [factiii.factiiiId])));
      const hasMore = posts.length > 1;
      const nextTimestamp = hasMore ? new Date(posts[posts.length - 1].createdAt).getTime() : undefined;
      if (hasMore) {
        posts.pop();
      }
      return {
        posts,
        nextTimestamp
      };
    };
    $[8] = factiii.factiiiId;
    $[9] = getPostParams;
    $[10] = props.params;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Date.now();
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = Boolean(localPreferenceState.postModeType) && Boolean(localPreferenceState.postFilterType) && currentUser.isAuthenticated !== null && expanded;
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: t4,
      getNextPageParam: _temp,
      enabled: t5,
      staleTime: 60000
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t6);
  let t7;
  if ($[17] !== currentUser.isAuthenticated || $[18] !== factiii.factiiiId) {
    t7 = ["factiii", "data", factiii.factiiiId, currentUser.isAuthenticated];
    $[17] = currentUser.isAuthenticated;
    $[18] = factiii.factiiiId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== currentUser.isAuthenticated || $[21] !== factiii.factiiiId || $[22] !== getFactiiiId) {
    t8 = async () => {
      const factiiiData = await getFactiiiId(factiii.factiiiId);
      if (factiiiData) {
        return {
          ...factiiiData,
          goals: currentUser.isAuthenticated ? (await unwrapFromResult(client.factiiis.factiiiGoals.query({
            factiiiId: factiii.factiiiId
          }))).goals : []
        };
      }
    };
    $[20] = currentUser.isAuthenticated;
    $[21] = factiii.factiiiId;
    $[22] = getFactiiiId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const t9 = Boolean(factiii.factiiiId) && expanded;
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      staleTime: 60000
    };
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    data: factiiiData_0
  } = useQuery(t10);
  const calculate = _temp2;
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[28] !== data || $[29] !== expanded || $[30] !== factiii.factiiiId || $[31] !== factiii.name || $[32] !== factiii.requirements || $[33] !== factiiiData_0?.avatarKey || $[34] !== factiiiData_0?.goals || $[35] !== fetchNextPage || $[36] !== hasNextPage || $[37] !== isFetching || $[38] !== isFetchingNextPage || $[39] !== localPreferenceState.postSortBy) {
    const items = data ? groupByFactiiiId(data.pages).find(f => f[0][0].factiiiId === factiii.factiiiId) : undefined;
    let t15;
    bb0: {
      if (!items) {
        t15 = 0;
        break bb0;
      }
      const calculateDaysBetween = _temp3;
      const lastBatch = items[items.length - 1];
      if (!lastBatch?.length) {
        t15 = 0;
        break bb0;
      }
      const lastPost = lastBatch[lastBatch.length - 1];
      if (!lastPost || !lastPost.postFactiiis?.length) {
        t15 = 0;
        break bb0;
      }
      t15 = calculateDaysBetween(new Date(lastPost.postFactiiis[0].createdAt || 0));
    }
    const getNumberOfDays = t15;
    t11 = factiii.factiiiId;
    t12 = "rounded-md bg-background-secondary shadow-lg";
    let t16;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = () => setExpanded(_temp4);
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    let t17;
    if ($[45] !== expanded) {
      t17 = <button className="mr-2 p-1 hover:text-primary" onClick={t16}>{expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>;
      $[45] = expanded;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    const t18 = `/fids/${factiii.factiiiId}`;
    const t19 = factiiiData_0?.avatarKey ?? "";
    let t20;
    if ($[47] !== factiii.factiiiId || $[48] !== t19) {
      t20 = <Avatar size={35} factiiiAvatarKey={t19} factiiiId={factiii.factiiiId} />;
      $[47] = factiii.factiiiId;
      $[48] = t19;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== t18 || $[51] !== t20) {
      t21 = <Link onClick={_temp5} href={t18}>{t20}</Link>;
      $[50] = t18;
      $[51] = t20;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    const t22 = `/fids/${factiii.factiiiId}`;
    let t23;
    if ($[53] !== factiii.name) {
      t23 = <p className="font-semibold leading-none">{factiii.name}</p>;
      $[53] = factiii.name;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== t22 || $[56] !== t23) {
      t24 = <div className="flex items-center"><Link onClick={_temp6} href={t22}>{t23}</Link></div>;
      $[55] = t22;
      $[56] = t23;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    let t25;
    if ($[58] !== factiiiData_0?.goals || $[59] !== localPreferenceState.postSortBy) {
      t25 = factiiiData_0?.goals && <div className="flex gap-x-2">{factiiiData_0?.goals.filter(goal => goal.type === FactiiiGoalType.DAILY && localPreferenceState.postSortBy === postDataSort.DAILY || goal.type === FactiiiGoalType.WEEKLY && localPreferenceState.postSortBy === postDataSort.WEEKLY || goal.type === FactiiiGoalType.MONTHLY && localPreferenceState.postSortBy === postDataSort.MONTHLY).map(_temp7)}</div>;
      $[58] = factiiiData_0?.goals;
      $[59] = localPreferenceState.postSortBy;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    let t26;
    if ($[61] !== t25) {
      t26 = <div className="mt-1 flex items-center"><div className="text-sm text-textSecondary/70">{t25}</div></div>;
      $[61] = t25;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    let t27;
    if ($[63] !== t24 || $[64] !== t26) {
      t27 = <div className="ml-4 flex flex-col">{t24}{t26}</div>;
      $[63] = t24;
      $[64] = t26;
      $[65] = t27;
    } else {
      t27 = $[65];
    }
    let t28;
    if ($[66] !== t17 || $[67] !== t21 || $[68] !== t27) {
      t28 = <div className="flex items-center">{t17}{t21}{t27}</div>;
      $[66] = t17;
      $[67] = t21;
      $[68] = t27;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    let t29;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <PlusCircle />;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    let t30;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <Button titleElement={t29} variant="SECONDARY" borderTransparent={false} classNames="hover:bg-background-light w-auto" onClick={() => setNewPostModalOpen(true)} />;
      $[71] = t30;
    } else {
      t30 = $[71];
    }
    const t31 = <div className="flex items-end gap-x-4"><div className="text-sm text-textSecondary/70">Total: {items?.length}</div>{t30}</div>;
    if ($[72] !== t28 || $[73] !== t31) {
      t13 = <div className="sticky top-[59px] mb-4 flex items-center justify-between rounded-md border-2 border-ds-border-standard bg-background-primary p-4">{t28}{t31}</div>;
      $[72] = t28;
      $[73] = t31;
      $[74] = t13;
    } else {
      t13 = $[74];
    }
    t14 = expanded && items && <div><div className="transition-all duration-300 ease-in-out">{items.map((group, i_0) => <div key={i_0} className="p-4"><h1 className="my-4 text-center font-semibold italic text-textSecondary/70">{(() => {
              const lastCreatedAt = group[group.length - 1]?.postFactiiis[group[group.length - 1].postFactiiis.length - 1].createdAt;
              const date = lastCreatedAt ? new Date(lastCreatedAt) : null;
              return date ? getTimeBySort(date, localPreferenceState.postSortBy) : null;
            })()}</h1><div className="overflow-hidden rounded-lg ring-2 ring-ds-border-standard"><table className="w-full border-collapse"><thead><tr><th className="border border-ds-border-standard p-2">User</th><th className="border border-ds-border-standard p-2">Time</th><th className="border border-ds-border-standard p-2">Count</th>{factiii.requirements.includes(FactiiiRequirement.INTEGER_REQUIRED) && <th className="border border-ds-border-standard p-2">Remaining</th>}</tr></thead><tbody>{group.flatMap((row, rowIndex) => row.postFactiiis.map((postFactiii, pfIndex) => {
                  const postUser = row.user;
                  return <tr key={`${rowIndex}-${pfIndex}`}><td className="border border-ds-border-standard p-2 text-center"><Link href={`/user/${postUser.username}`}><div className="flex items-center space-x-2"><Avatar size={32} avatarKey={postUser.avatarKey ?? null} robohash={postUser.robohash ?? ""} isBot={postUser.tag === "BOT"} userId={postUser.id} /><div className="text-sm font-emphasis">@{postUser.username}</div></div></Link></td><td className="border border-ds-border-standard p-2 text-center">{postFactiii.createdAt ? new Date(postFactiii.createdAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      }) : "unknown"}</td><td className="border border-ds-border-standard p-2 text-center">{postFactiii.number ?? postFactiii.data}</td>{row.requirements.includes(FactiiiRequirement.INTEGER_REQUIRED) && <td className="border border-ds-border-standard p-2 text-center">{factiiiData_0?.goals?.filter(goal_0 => goal_0.type === FactiiiGoalType.DAILY && localPreferenceState.postSortBy === postDataSort.DAILY || goal_0.type === FactiiiGoalType.WEEKLY && localPreferenceState.postSortBy === postDataSort.WEEKLY || goal_0.type === FactiiiGoalType.MONTHLY && localPreferenceState.postSortBy === postDataSort.MONTHLY).map(filteredGoal_0 => <div key={filteredGoal_0.id}>{Math.max(0, Number(filteredGoal_0.data) - calculate(group.flat(), rowIndex))}</div>)}</td>}</tr>;
                }))}</tbody></table></div></div>)}</div><div className="flex items-center gap-x-2 p-4 text-sm"><div className="flex space-x-1"><p className="font-semibold">{getNumberOfDays}</p><p>{getNumberOfDays > 1 ? "days" : "day"} of posts loaded</p></div>{hasNextPage && <>•<Button text={isFetchingNextPage ? "" : "Load More"} variant="SECONDARY" loading={isFetchingNextPage} onClick={() => void fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage || isFetching} classNames="!p-0 hover:bg-transparent !text-primary hover:underline" /></>}</div></div>;
    $[28] = data;
    $[29] = expanded;
    $[30] = factiii.factiiiId;
    $[31] = factiii.name;
    $[32] = factiii.requirements;
    $[33] = factiiiData_0?.avatarKey;
    $[34] = factiiiData_0?.goals;
    $[35] = fetchNextPage;
    $[36] = hasNextPage;
    $[37] = isFetching;
    $[38] = isFetchingNextPage;
    $[39] = localPreferenceState.postSortBy;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
  }
  let t15;
  if ($[75] !== currentUser.isAuthenticated || $[76] !== factiiiData_0 || $[77] !== newPostModalOpen) {
    t15 = currentUser.isAuthenticated && newPostModalOpen && <NewPostModal closeHandler={() => setNewPostModalOpen(false)} pinnedFactiii={factiiiData_0} />;
    $[75] = currentUser.isAuthenticated;
    $[76] = factiiiData_0;
    $[77] = newPostModalOpen;
    $[78] = t15;
  } else {
    t15 = $[78];
  }
  let t16;
  if ($[79] !== t11 || $[80] !== t12 || $[81] !== t13 || $[82] !== t14 || $[83] !== t15) {
    t16 = <div key={t11} className={t12}>{t13}{t14}{t15}</div>;
    $[79] = t11;
    $[80] = t12;
    $[81] = t13;
    $[82] = t14;
    $[83] = t15;
    $[84] = t16;
  } else {
    t16 = $[84];
  }
  return t16;
};
export default DataItem;
function _temp(lastPage) {
  const posts_0 = lastPage?.posts ?? [];
  if (posts_0.length === 0) {
    return;
  }
  return lastPage.nextTimestamp;
}
function _temp2(dataFactiii, index) {
  let sum = 0;
  for (let i = dataFactiii.length - 1; i >= index; i--) {
    const factiii_0 = dataFactiii[i];
    if (factiii_0?.postFactiiis) {
      for (const pf of factiii_0.postFactiiis) {
        sum = sum + (pf.number ?? 0);
      }
    }
  }
  return sum;
}
function _temp3(startDate) {
  const currentDate = new Date();
  const start = new Date(startDate);
  const differenceInTime = currentDate.getTime() - start.getTime();
  const differenceInDays = Math.ceil(differenceInTime / 86400000);
  return differenceInDays;
}
function _temp4(prev) {
  return !prev;
}
function _temp5(e) {
  return e.stopPropagation();
}
function _temp6(e_0) {
  return e_0.stopPropagation();
}
function _temp7(filteredGoal) {
  return <div key={filteredGoal.id}>Goal: {filteredGoal.data}</div>;
}